/**
 * Policy Intelligence API Service
 *
 * Calls the Policy Intelligence Network endpoints for effectiveness scoring,
 * recommendations, drift detection, and feedback.
 */
import { API_BASE_URL, getAccessToken } from './api';
import { getTenantIdentifier } from './tenant/tenantAccessor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PolicyIntelligenceSummary {
  effectivenessScore: number;
  coverageGapScore: number;
  driftScore: number;
  /**
   * Null when no recommendation has been accepted or dismissed yet — i.e. nothing has been
   * decided, so there is no rate to report. Deliberately NOT zero: a zero here would read as
   * "measured, and nobody accepts our recommendations", which is a different and worse claim.
   * Render the not-measured state rather than coercing it to a number.
   */
  recommendationAcceptanceRate: number | null;
  benchmarkScore: number | null;
  totalPolicies: number;
  totalEvaluations: number;
  periodDays: number;
}

export interface PolicyEffectivenessRecord {
  policyId: string;
  policyName: string;
  family: string;
  classification: string;
  score: number;
  executions: number;
  truePositives: number;
  falsePositives: number;
  trend: 'improving' | 'stable' | 'declining';
}

export interface PolicyEffectivenessResult {
  records: PolicyEffectivenessRecord[];
  averageScore: number;
  periodDays: number;
}

export interface PolicyRecommendation {
  id: string;
  title: string;
  whyItMatters: string;
  suggestedAction: string;
  estimatedImpact: 'High' | 'Medium' | 'Low';
  confidence: number;
  policyFamily: string;
  type: string;
  status: 'Pending' | 'Accepted' | 'Dismissed';
  createdAt: string;
}

export interface PolicyRecommendationsResult {
  recommendations: PolicyRecommendation[];
  totalCount: number;
}

export interface PolicyDriftRecord {
  policyId: string;
  policyName: string;
  driftType: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  detectedAt: string;
  description: string;
}

export interface PolicyDriftResult {
  records: PolicyDriftRecord[];
  totalDriftScore: number;
}

export interface PolicyFeedbackRequest {
  recommendationId: string;
  rating: 'helpful' | 'not_helpful';
  comment?: string;
}

/**
 * A single peer-comparison metric. The peer-side fields (`peerMedian`, `peerP25`, `peerP75`,
 * `tenantPercentile`, `sampleSize`, `cohortDescription`) are OPTIONAL and may legitimately be
 * absent — the F052 benchmark engine enforces k-anonymity suppression, so a metric with an
 * insufficient peer cohort returns without a comparable peer value rather than a fabricated
 * zero. Consumers must never substitute a default when these are missing; render the honest
 * "not available" state instead (see `BenchmarkCard`).
 */
export interface BenchmarkMetric {
  policyFamily: string;
  metricName: string;
  tenantValue: number;
  peerMedian?: number | null;
  peerP25?: number | null;
  peerP75?: number | null;
  tenantPercentile?: number | null;
  classification?: 'stronger_than_peers' | 'weaker_than_peers' | 'materially_under_covered';
  /** Anonymised peer count backing `peerMedian` — required before peer data may be shown (k-anonymity). */
  sampleSize?: number;
  /** e.g. "financial services, APAC" — required before peer data may be shown. */
  cohortDescription?: string;
  /** True when the API suppressed peer comparison for this metric (insufficient cohort size). */
  suppressed?: boolean;
  /** Optional customer-safe explanation for why the metric was suppressed. */
  suppressionReason?: string;
}

export interface BenchmarkResult {
  metrics: BenchmarkMetric[];
  scope?: string;
  strongerCount?: number;
  weakerCount?: number;
  underCoveredCount?: number;
}

export interface PolicyIntelligenceSettings {
  networkMode: string;
  allowAnonymisedTelemetry: boolean;
  allowBenchmarking: boolean;
}

export interface UpdatePolicyIntelligenceSettingsRequest {
  networkMode: string;
  allowAnonymisedTelemetry: boolean;
  allowBenchmarking: boolean;
}

// ---------------------------------------------------------------------------
// HTTP helper (mirrors pattern from api.ts)
// ---------------------------------------------------------------------------

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = await getAccessToken();
  const tenantId = getTenantIdentifier();
  const correlationId = crypto.randomUUID();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Correlation-Id': correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-Id': tenantId }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(error.message || `HTTP ${response.status}`) as Error & { status: number };
    apiError.status = response.status;
    throw apiError;
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const policyIntelligenceApi = {
  async getSummary(): Promise<PolicyIntelligenceSummary> {
    return fetchWithAuth('/portal/policy-intelligence/summary');
  },

  async getEffectiveness(): Promise<PolicyEffectivenessResult> {
    return fetchWithAuth('/portal/policy-intelligence/effectiveness');
  },

  async getRecommendations(): Promise<PolicyRecommendationsResult> {
    return fetchWithAuth('/portal/policy-intelligence/recommendations');
  },

  async getDrift(): Promise<PolicyDriftResult> {
    return fetchWithAuth('/portal/policy-intelligence/drift');
  },

  async acceptRecommendation(id: string): Promise<void> {
    return fetchWithAuth(`/portal/policy-intelligence/recommendations/${id}/accept`, {
      method: 'POST',
    });
  },

  async dismissRecommendation(id: string): Promise<void> {
    return fetchWithAuth(`/portal/policy-intelligence/recommendations/${id}/dismiss`, {
      method: 'POST',
    });
  },

  async deferRecommendation(id: string, deferUntil: string): Promise<void> {
    return fetchWithAuth(`/portal/policy-intelligence/recommendations/${id}/defer`, {
      method: 'POST',
      body: JSON.stringify({ deferUntil }),
    });
  },

  async submitFeedback(feedback: PolicyFeedbackRequest): Promise<void> {
    return fetchWithAuth('/portal/policy-intelligence/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  },

  async getBenchmarks(scope?: string): Promise<BenchmarkResult> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '?scope=global';
    return fetchWithAuth(`/portal/policy-intelligence/benchmarks${params}`);
  },

  async getSettings(): Promise<PolicyIntelligenceSettings> {
    return fetchWithAuth('/portal/policy-intelligence/settings');
  },

  async updateSettings(data: UpdatePolicyIntelligenceSettingsRequest): Promise<void> {
    return fetchWithAuth('/portal/policy-intelligence/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};
